/**
 * One document, a chain of versions (DESIGN UD-27, `docs/plan/VERSION-CHAIN-DESIGN.md`): the upload is version 0, and every
 * finished bench action makes the next version from the latest one. Every later action and every view works on the latest
 * version; every before/after comparison is with version 0. This file holds the record and its pure rules (tested in Node);
 * `store.ts` keeps one record per document and `chain-control.ts` drives it.
 */
import { type MarkdownEdit, cleanEdits } from '../../shared/md-review.ts';
import type { AnalysisSummary } from '../../shared/summary.ts';
import type { BenchJob } from './job.ts';
import type { RewriteScope } from './state.ts';

/** Schema 2 (MDREVIEW, UD-31) adds each version's optional `mdEdits`; schema 1 chains are read as they are (`migrateChain`). */
export const CHAIN_SCHEMA = 2;

/** What made a version: the local pass (with its count of changes), or a deep-rewrite job. */
export type VersionMaker =
	| { kind: 'local'; changes: number }
	| {
			kind: 'job';
			jobId: string;
			scope: RewriteScope;
			/** The job ran with the editorial guardrails (DESIGN ED-191): stored only when on, so older chains read as off. */
			guardrails?: true;
	  };

export interface ChainVersion {
	/** 1, 2, …: version n is `versions[n - 1]`. */
	n: number;
	/** The version's text, as the step produced it (with the latest step's reverts applied). */
	text: string;
	/** `textHash(text)`; version 0's hash is the chain's `root`. */
	hash: string;
	words: number;
	made: VersionMaker;
	/** Units of the step's job the customer reverted (the latest step only, P2). */
	reverted?: string[];
	/**
	 * The operator's edits of the version's inline Markdown (UD-31, `md-review.ts`): each replaces one paragraph's automatic
	 * Markdown in every download of this version while the paragraph's text is the one it was made against.
	 */
	mdEdits?: MarkdownEdit[];
	/** ISO 8601. */
	createdAt: string;
}

export interface DocChain {
	schema: typeof CHAIN_SCHEMA;
	/** Version 0's `meta.docHash`: the document's identity, and the record's key. */
	root: string;
	/** Versions 1 to n, oldest first; empty when only the original exists. */
	versions: ChainVersion[];
	/** Jobs whose version was undone (the jobs list says so; Continue from this result brings one back). */
	undone: string[];
	/** Version 0's plain-text summary: every overview's "before" (P3), measured once. */
	origin?: AnalysisSummary;
	updatedAt: string;
}

/** The version a job started from: its number and its hash (a number alone is ambiguous after an Undo). */
export interface VersionRef {
	n: number;
	hash: string;
}

/** FNV-1a over the UTF-16 code units, as 8 hex digits: a version's identity (a job's `base` names it with its number). */
export function textHash(text: string): string {
	let h = 0x811c9dc5;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return (h >>> 0).toString(16).padStart(8, '0');
}

export const countWords = (text: string): number => text.split(/\s+/).filter(Boolean).length;

/**
 * A chain read from storage, at the current schema: a schema 1 chain (no Markdown edits) is kept as it is, a version's edits
 * are cleaned of anything malformed. Undefined for a record that is not a chain, or one a later schema wrote.
 */
export function migrateChain(raw: unknown): DocChain | undefined {
	if (!raw || typeof raw !== 'object') return undefined;
	const c = raw as Partial<DocChain>;
	if (typeof c.root !== 'string' || !Array.isArray(c.versions)) return undefined;
	if (typeof c.schema === 'number' && c.schema > CHAIN_SCHEMA) return undefined;
	const versions = c.versions.map((v) => {
		const { mdEdits, ...rest } = v;
		const edits = cleanEdits(mdEdits);
		return edits.length > 0 ? { ...rest, mdEdits: edits } : rest;
	});
	return { ...(c as DocChain), schema: CHAIN_SCHEMA, versions };
}

/** Version `n` with its Markdown edits replaced (none removes them). Another version, or an unknown `n`, is untouched. */
export function setMarkdownEdits(
	chain: DocChain,
	n: number,
	edits: readonly MarkdownEdit[],
	now = new Date().toISOString()
): DocChain {
	const v = chain.versions[n - 1];
	if (!v) return chain;
	const { mdEdits: _was, ...rest } = v;
	const next: ChainVersion = edits.length > 0 ? { ...rest, mdEdits: cleanEdits(edits) } : rest;
	const versions = chain.versions.map((x, i) => (i === n - 1 ? next : x));
	return { ...chain, versions, updatedAt: now };
}

/** The edits stored for the version whose text hashes to `hash` (the latest of them), or none. */
export function editsForHash(chain: DocChain | null, hash: string): MarkdownEdit[] {
	const vs = chain?.versions.filter((x) => x.hash === hash) ?? [];
	return vs.at(-1)?.mdEdits ?? [];
}

export function emptyChain(root: string, now = new Date().toISOString()): DocChain {
	return { schema: CHAIN_SCHEMA, root, versions: [], undone: [], updatedAt: now };
}

/** The latest version's number and hash (version 0 when the chain is empty). */
export function latestRef(chain: DocChain): VersionRef {
	const v = chain.versions.at(-1);
	return v ? { n: v.n, hash: v.hash } : { n: 0, hash: chain.root };
}

/** The latest version's text, or null when it is version 0 (whose text is the open document's). */
export const latestText = (chain: DocChain): string | null => chain.versions.at(-1)?.text ?? null;

/** The version a job started from: its `base`, else version 0 (a job from before the chain: a one-step chain on its upload). */
export function baseOf(job: Pick<BenchJob, 'base' | 'docHash'>): VersionRef {
	return job.base ?? { n: 0, hash: job.docHash };
}

/** Whether `ref` is a version of this chain (version 0, or version n with the same hash). */
export function holds(chain: DocChain, ref: VersionRef): boolean {
	if (ref.n === 0) return ref.hash === chain.root;
	return chain.versions[ref.n - 1]?.hash === ref.hash;
}

/** A step built on `base` may become the next version only while `base` is still the latest version. */
export function canAppend(chain: DocChain, base: VersionRef): boolean {
	const l = latestRef(chain);
	return l.n === base.n && l.hash === base.hash;
}

/** What a finished job makes of a version: its id and scope, and the guardrails flag when it ran with them. */
export const makerOfJob = (job: Pick<BenchJob, 'id' | 'scope' | 'guardrails'>): VersionMaker => ({
	kind: 'job',
	jobId: job.id,
	scope: job.scope,
	...(job.guardrails ? { guardrails: true as const } : {}),
});

export function append(
	chain: DocChain,
	step: { text: string; made: VersionMaker },
	now = new Date().toISOString()
): DocChain {
	const n = chain.versions.length + 1;
	const v: ChainVersion = {
		n,
		text: step.text,
		hash: textHash(step.text),
		words: countWords(step.text),
		made: step.made,
		createdAt: now,
	};
	const made = step.made;
	const undone =
		made.kind === 'job' ? chain.undone.filter((id) => id !== made.jobId) : chain.undone;
	return { ...chain, versions: [...chain.versions, v], undone, updatedAt: now };
}

/** Removes the latest version; its job, if any, is recorded as undone. An empty chain is returned as it is. */
export function undo(chain: DocChain, now = new Date().toISOString()): DocChain {
	const last = chain.versions.at(-1);
	if (!last) return chain;
	const undone =
		last.made.kind === 'job' && !chain.undone.includes(last.made.jobId)
			? [...chain.undone, last.made.jobId]
			: chain.undone;
	return { ...chain, versions: chain.versions.slice(0, -1), undone, updatedAt: now };
}

/** The latest version with its text replaced (a revert in the latest step) and the reverted units recorded. */
export function reviseLatest(
	chain: DocChain,
	text: string,
	reverted: readonly string[],
	now = new Date().toISOString()
): DocChain {
	const last = chain.versions.at(-1);
	if (!last) return chain;
	const { reverted: _was, ...rest } = last;
	const v: ChainVersion = {
		...rest,
		text,
		hash: textHash(text),
		words: countWords(text),
		...(reverted.length > 0 ? { reverted: [...reverted] } : {}),
	};
	return { ...chain, versions: [...chain.versions.slice(0, -1), v], updatedAt: now };
}

/** The version a job made in this chain, or null. */
export function versionOfJob(chain: DocChain, jobId: string): ChainVersion | null {
	return chain.versions.find((v) => v.made.kind === 'job' && v.made.jobId === jobId) ?? null;
}

/** Whether the job's output is the latest version (its lens may revert passages, P2). */
export function isLatestStep(chain: DocChain, jobId: string): boolean {
	const last = chain.versions.at(-1);
	return last?.made.kind === 'job' && last.made.jobId === jobId;
}

/** The text a finished job produced, or undefined. */
export const outputOf = (job: Pick<BenchJob, 'resultText' | 'report'>): string | undefined =>
	job.resultText ?? job.report?.text;

/**
 * A finished job outside the chain whose base is the latest version can become the next version ("Continue from this
 * result"): a job that finished after another step was added, a job whose version was undone, or a job from before the
 * chain on the original.
 */
export function canAdopt(chain: DocChain, job: BenchJob): boolean {
	if (job.status !== 'done' || outputOf(job) === undefined) return false;
	if (job.docHash !== chain.root || versionOfJob(chain, job.id)) return false;
	return canAppend(chain, baseOf(job));
}

export function adopt(chain: DocChain, job: BenchJob, now = new Date().toISOString()): DocChain {
	const text = outputOf(job);
	if (!canAdopt(chain, job) || text === undefined) return chain;
	return append(chain, { text, made: makerOfJob(job) }, now);
}

/**
 * Where a job stands in its document's chain, for the jobs list: the version it started from, and either the version it made,
 * or why it made none (still running or unfinished, undone, or left out because another step came first).
 */
export type JobPlace =
	| { base: number; made: number }
	| { base: number; made: null; why: 'unfinished' | 'undone' | 'outside' | 'other-document' };

export function jobPlace(chain: DocChain | null, job: BenchJob): JobPlace {
	const base = baseOf(job).n;
	if (!chain || chain.root !== job.docHash) return { base, made: null, why: 'other-document' };
	const v = versionOfJob(chain, job.id);
	if (v) return { base, made: v.n };
	if (job.status !== 'done') return { base, made: null, why: 'unfinished' };
	if (chain.undone.includes(job.id)) return { base, made: null, why: 'undone' };
	return { base, made: null, why: 'outside' };
}
